// =============================================================================
// Portal Order Detail Page — Admin-style layout (two-column, cards, timeline)
// =============================================================================

import { useParams, useNavigate, useSearchParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  Spinner,
  Modal,
  ModalFooter,
} from '@tkpod/ui';
import type { DesignPartInfo } from '@tkpod/ui';
import { useApiClient, ApiClientError } from '@tkpod/api-client';
import {
  getCancelAbility,
  isEditable,
  SHOPIFY_MAX_TAG_LENGTH,
  ORDER_STATUS_MERCHANT_VARIANT,
  getMerchantOrderStatusLabel,
  checkoutFulfillmentDisplay,
  orderItemDisplayName,
} from '@tkpod/shared';
import { useState, useEffect, useCallback, useRef, type FormEvent, type ReactNode } from 'react';
import { EventTimeline } from '../components/order/EventTimeline';
import { OrderSummaryCard } from '../components/order/OrderSummaryCard';
import { WholesaleCostCard } from '../components/order/WholesaleCostCard';
import {
  RevisionTimeline,
  type RevisionEntry,
} from '../components/order/RevisionTimeline';

interface OrderItem {
  id: string;
  title: string;
  /** Catalog product a PRODUCT line is built from. Null for custom products. */
  catalogTitle?: string | null;
  /** Ordered variant options label, e.g. "White / S" (apparel/POD lines). */
  variantTitle?: string | null;
  variantSnapshot?: unknown | null;
  /** Raw option snapshot — record (Shopify) or `[{name,value}]` (manual). */
  productOptions?: unknown | null;
  sku: string | null;
  quantity: number;
  cancelledQty?: number;
  unitPrice: string;
  totalPrice: string;
  uptId?: string | null;
  uptName: string | null;
  presetName: string | null;
  providerId: string | null;
  providerName: string | null;
  weightOz?: number | null;
  routingSource?: string | null;
  designId?: string | null;
  designRefId?: string | null;
  partKey?: string | null;
  designPreviewUrlStable?: string | null;
  designExportUrlStable?: string | null;
  designPreviewUrl?: string | null;
  /** PRODUCT-context line preview: ordered variant's hero composite. */
  variantPreviewUrl?: string | null;
  exportId?: string | null;
  exportStatus?: string | null;
  exportOutputUrl?: string | null;
  exportPreviewUrl?: string | null;
  sheetIndex?: number | null;
  designContext?: string | null;
  customization?: {
    selections: { label: string; value: string }[];
    uploads: {
      token?: string | null;
      label?: string | null;
      fileName: string | null;
      mimeType?: string | null;
      sizeBytes?: number | null;
      widthPx?: number | null;
      heightPx?: number | null;
      scanStatus?: string | null;
      versionNo: number | null;
      thumbUrl: string | null;
      previewUrl: string | null;
      downloadUrl: string | null;
    }[];
  } | null;
  designParts?: DesignPartInfo[];
  /** Order-time fulfillment snapshot — drives the Self-fulfill line badge. */
  fulfillmentProvider?: 'TK_POD' | 'SELF';
  fulfillmentMethod?: 'AUTO' | 'MANUAL';
  /** Whether this line has entered production (any provider submission). */
  inProduction?: boolean;
  /**
   * Server-computed post-order edit verdict for the PORTAL surface. Mirrors the
   * SAVE endpoint exactly so the button state never diverges from enforcement.
   */
  editPermission?: {
    canEdit: boolean;
    reason: string | null;
    message: string | null;
  } | null;
  /**
   * Customer-facing Edit link (Shopify App Proxy URL) for STOREFRONT lines of
   * Shopify orders — surfaced on the summary card as an open + copy row.
   */
  customerEditUrl?: string | null;
}

interface ExportStatus {
  orderId: string;
  status: 'NONE' | 'PENDING' | 'READY' | 'FAILED';
  ready: boolean;
  failed: boolean;
  hasFiles: boolean;
  totals: {
    items: number;
    readyItems: number;
    pendingItems: number;
    failedItems: number;
  };
}

interface ShipmentInfo {
  id: string;
  carrier: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  status: string;
  shippedAt: string | null;
  deliveredAt: string | null;
  fulfilledByProvider: { id: string; name: string } | null;
  items: Array<{ orderItemId: string; quantity: number }>;
  createdAt: string;
}

interface OrderDetail {
  id: string;
  orderNumber: string;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  status: string;
  fulfillmentStatus: string;
  source: string;
  subtotal: string;
  total: string;
  currency: string;
  // Wholesale billing snapshot — what Transfer Kingdom will invoice the
  // merchant for this order. Null for legacy orders ingested before the
  // snapshot was introduced.
  wholesaleProductionCost?: string | null;
  wholesaleShippingCost?: string | null;
  wholesaleTotalCost?: string | null;
  wholesaleCostComputedAt?: string | null;
  // Wholesale settlement lifecycle (what TK collects from the merchant) —
  // decoupled from the Shopify customer payment status.
  wholesaleChargeStatus?: string | null;
  chargeHoldReason?: string | null;
  lastChargeFailureCode?: string | null;
  // Order-level fulfillment shape derived from line snapshots.
  fulfillmentMix?: 'ALL_TK_POD' | 'ALL_SELF' | 'MIXED';
  fulfillmentRequestedAt?: string | null;
  /** Shopify checkout delivery method (PICK_UP, SHIPPING, …). */
  shopifyDeliveryMethod?: string | null;
  shippingMethod?: string | null;
  /** Shopify-synced order tags (two-way for Shopify orders). */
  tags?: string[];
  shippingAddress: Record<string, unknown> | null;
  resolvedProvider: { id: string; name: string } | null;
  resolvedWarehouse: { id: string; name: string; code: string } | null;
  overrideProvider: { id: string; name: string } | null;
  overrideWarehouse: { id: string; name: string; code: string } | null;
  items: OrderItem[];
  shipments?: ShipmentInfo[];
  events: {
    id: string;
    type: string;
    data: Record<string, unknown> | null;
    userName: string | null;
    createdAt: string;
  }[];
  createdAt: string;
}

export function PortalOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [restrictionToast, setRestrictionToast] = useState<string | null>(null);
  const restrictionTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const showRestrictionToast = useCallback((msg: string) => {
    if (restrictionTimer.current) clearTimeout(restrictionTimer.current);
    setRestrictionToast(msg);
    restrictionTimer.current = setTimeout(() => setRestrictionToast(null), 5000);
  }, []);

  useEffect(() => () => {
    if (restrictionTimer.current) clearTimeout(restrictionTimer.current);
  }, []);

  useEffect(() => {
    if (searchParams.get('created') === '1') {
      setSuccessMessage(
        'Order created successfully! Provider assignment in progress.',
      );
      const next = new URLSearchParams(searchParams);
      next.delete('created');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const orderQuery = useQuery({
    queryKey: ['portal', 'orders', id],
    queryFn: () => api.get<{ data: OrderDetail }>(`/api/v1/orders/${id}`),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data?.data;
      const items = data?.items ?? [];
      // Legacy lane: an item still bound to a per-order Export row that has not
      // settled (historical gangsheet/DTF orders created before the DesignPart
      // lane). New apparel/gangsheet lines carry no exportId, so this alone no
      // longer covers them.
      const legacyPending = items.some(
        (i: OrderItem) => i.exportId && i.exportStatus !== 'READY' && i.exportStatus !== 'FAILED',
      );
      // DesignPart lane: the order is mid-export (apparel SIDE / gangsheet SHEET
      // parts still rendering). These statuses flip server-side to EXPORTED /
      // EXPORT_FAILED, so keep polling to surface the transition live.
      const exporting =
        data?.status === 'EXPORT_QUEUED' || data?.status === 'EXPORT_PROCESSING';
      return legacyPending || exporting ? 5000 : false;
    },
  });

  const hasDesignFiles =
    orderQuery.data?.data?.items?.some(
      (it: OrderItem) => (it.designParts?.length ?? 0) > 0,
    ) ?? false;
  const hasOptionFiles =
    orderQuery.data?.data?.items?.some(
      (it: OrderItem) => (it.customization?.uploads?.length ?? 0) > 0,
    ) ?? false;
  const hasDownloadableFiles = hasDesignFiles || hasOptionFiles;

  // Lightweight readiness poll dedicated to the smart download control. Stops
  // as soon as the bundle is READY or terminally FAILED.
  const exportStatusQuery = useQuery({
    queryKey: ['portal', 'orders', id, 'export-status'],
    queryFn: () =>
      api.get<{ data: ExportStatus }>(`/api/v1/orders/${id}/export-status`),
    enabled: !!id && hasDownloadableFiles,
    refetchInterval: (query) => {
      const s = query.state.data?.data;
      // Stop polling on terminal states. NONE means no in-scope item produces
      // files for this audience (e.g. POD-only order in the portal) — it
      // never transitions, so polling would spin forever.
      return s && (s.ready || s.failed || s.status === 'NONE') ? false : 5000;
    },
  });

  // Revision history (post-order design edits) for the RevisionTimeline.
  const revisionsQuery = useQuery({
    queryKey: ['portal', 'orders', id, 'revisions'],
    queryFn: () =>
      api.get<{ data: RevisionEntry[] }>(`/api/v1/orders/${id}/revisions`),
    enabled: !!id,
  });

  // Launch the designer to edit a line's design (post-order revision flow).
  // Mints an `edit-order` launch token bound to THIS order line, then redirects
  // to the designer SPA. The API re-validates the line + permission server-side.
  const editDesignMutation = useMutation({
    mutationFn: async (item: OrderItem) => {
      const res = await api.post<{
        data: { token: string; designerUrl: string; expiresAt: number };
      }>('/api/v1/auth/designer-launch-token', {
        uptId: item.uptId,
        returnUrl: `${window.location.origin}/orders/${id}`,
        orderId: id,
        orderItemId: item.id,
        purpose: 'edit-order',
      });
      return { res: res.data, item };
    },
    onSuccess: ({ res, item }) => {
      const url = new URL(`${res.designerUrl}/designer`);
      url.searchParams.set('context', 'edit-order');
      url.searchParams.set('token', res.token);
      url.searchParams.set('orderId', id!);
      url.searchParams.set('itemId', item.id);
      if (item.uptId) url.searchParams.set('uptId', item.uptId);
      url.searchParams.set('returnUrl', `${window.location.origin}/orders/${id}`);
      window.location.href = url.toString();
    },
    onError: (err) => {
      showRestrictionToast(
        err instanceof ApiClientError && err.apiError?.details?.length
          ? err.apiError.details.map((d) => d.message).join('. ')
          : (err as Error)?.message ?? 'Failed to open the designer.',
      );
    },
  });

  const refreshOrder = (freshData?: { data: OrderDetail }) => {
    if (freshData) {
      queryClient.setQueryData(['portal', 'orders', id], freshData);
    }
    void queryClient.invalidateQueries({ queryKey: ['portal', 'orders'] });
  };

  const cancelMutation = useMutation({
    mutationFn: () => api.post<{ data: OrderDetail }>(`/api/v1/orders/${id}/cancel`),
    onSuccess: (res) => {
      setCancelConfirmOpen(false);
      const newStatus = res?.data?.status;
      setSuccessMessage(
        newStatus === 'CANCEL_REQUESTED'
          ? 'Cancellation request has been submitted.'
          : 'Order has been cancelled.',
      );
      refreshOrder(res);
    },
  });

  // Re-render all print files under a new revision. The API only allows this
  // from a terminal export state (EXPORTED / EXPORT_FAILED / FAILED).
  const regenerateExportsMutation = useMutation({
    mutationFn: () =>
      api.post<{ orderId: string; revision: number; requeued: number }>(
        `/api/v1/orders/${id}/regenerate-exports`,
      ),
    onSuccess: (res) => {
      setSuccessMessage(
        `Regenerating ${res.requeued} print file(s) — revision ${res.revision}.`,
      );
      void queryClient.refetchQueries({ queryKey: ['portal', 'orders', id] });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'orders', id, 'export-status'] });
    },
  });

  const extractTagError = (err: unknown, fallback: string) =>
    err instanceof ApiClientError && err.apiError?.details?.length
      ? err.apiError.details.map((d) => d.message).join('. ')
      : (err as Error)?.message ?? fallback;

  const addTagMutation = useMutation({
    mutationFn: (tag: string) =>
      api.post<{ data: OrderDetail }>(`/api/v1/orders/${id}/tags`, { tag }),
    onSuccess: (res) => {
      setSuccessMessage('Tag added.');
      refreshOrder(res);
    },
    onError: (err) => showRestrictionToast(extractTagError(err, 'Failed to add tag.')),
  });

  const removeTagMutation = useMutation({
    mutationFn: (tag: string) =>
      api.post<{ data: OrderDetail }>(`/api/v1/orders/${id}/tags/remove`, { tag }),
    onSuccess: (res) => {
      setSuccessMessage('Tag removed.');
      refreshOrder(res);
    },
    onError: (err) => showRestrictionToast(extractTagError(err, 'Failed to remove tag.')),
  });

  const [tagInput, setTagInput] = useState('');

  const [addressEditOpen, setAddressEditOpen] = useState(false);
  const [addressForm, setAddressForm] = useState({
    name: '',
    address1: '',
    address2: '',
    city: '',
    province: '',
    zip: '',
    country: '',
    phone: '',
  });

  const openAddressEdit = useCallback(() => {
    const a = (orderQuery.data?.data?.shippingAddress ?? {}) as Record<string, string>;
    setAddressForm({
      name: a.name ?? orderQuery.data?.data?.customerName ?? '',
      // Shopify orders store street1/street2; portal orders address1/address2.
      address1: a.street1 ?? a.address1 ?? '',
      address2: a.street2 ?? a.address2 ?? '',
      city: a.city ?? '',
      province: a.province ?? '',
      zip: a.zip ?? '',
      country: a.country ?? '',
      phone: a.phone ?? '',
    });
    setAddressEditOpen(true);
  }, [orderQuery.data]);

  const addressMutation = useMutation({
    mutationFn: (payload: Record<string, string>) =>
      api.patch<{ data: OrderDetail }>(`/api/v1/orders/${id}/address`, payload),
    onSuccess: (res) => {
      setAddressEditOpen(false);
      setSuccessMessage('Shipping address updated.');
      refreshOrder(res);
    },
  });

  const handleAddressSave = useCallback(() => {
    const payload: Record<string, string> = {};
    for (const [key, value] of Object.entries(addressForm)) {
      if (value.trim()) payload[key] = value.trim();
    }
    addressMutation.mutate(payload);
  }, [addressForm, addressMutation]);

  // Merchant self-service wholesale settlement retry. Idempotent on the API
  // (ALREADY_PAID is a safe no-op), so we surface the resulting state via the
  // themed success/error banner — never a native popup (ui-dialogs rule).
  const chargeMutation = useMutation({
    mutationFn: () =>
      api.post<{
        status: string;
        reasonCode?: string;
        reason?: string;
      }>(`/api/v1/orders/${id}/charge`),
    onSuccess: (res) => {
      const outcome = res?.status;
      if (outcome === 'PAID') {
        setSuccessMessage('Payment collected — production can now start.');
      } else if (outcome === 'ALREADY_PAID') {
        setSuccessMessage('This order is already settled.');
      } else if (outcome === 'ON_HOLD') {
        showRestrictionToast(
          'We still could not collect the payment. Check your wallet balance or default card, then try again.',
        );
      } else {
        setSuccessMessage('Retry started — settlement status will update shortly.');
      }
      void queryClient.invalidateQueries({ queryKey: ['portal', 'orders'] });
    },
    onError: (err) => {
      showRestrictionToast(
        err instanceof ApiClientError && err.apiError?.details?.length
          ? err.apiError.details.map((d) => d.message).join('. ')
          : (err as Error)?.message ?? 'Failed to retry the charge.',
      );
    },
  });

  if (orderQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  if (orderQuery.isError) {
    return (
      <div className="py-24 text-center">
        <p className="text-sm text-danger mb-3">Failed to load order.</p>
        <Button variant="outline" size="sm" onClick={() => orderQuery.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const order = orderQuery.data?.data;
  if (!order) {
    return (
      <div className="py-24 text-center text-sm text-text-tertiary">
        Order not found.
      </div>
    );
  }

  const isAllSelf = order.fulfillmentMix === 'ALL_SELF';
  const isMixedFulfillment = order.fulfillmentMix === 'MIXED';
  const checkoutMethod = checkoutFulfillmentDisplay({
    shopifyDeliveryMethod: order.shopifyDeliveryMethod,
    shippingMethod: order.shippingMethod,
  });
  const isAwaitingApproval = order.status === 'AWAITING_FULFILLMENT_REQUEST';

  // Fully self-fulfilled orders are produced, shipped, and cancelled by the
  // merchant in Shopify — TK-side cancel actions don't apply to them.
  const cancelAbility = isAllSelf ? 'none' : getCancelAbility(order.status);
  const canDirectCancel = cancelAbility === 'direct';
  const canRequestCancel = cancelAbility === 'request';
  const canCancel = cancelAbility !== 'none';
  const canEditAddress = isEditable(order.status, 'address');
  const canEditTags = isEditable(order.status, 'tags');
  const isTerminal = ['DELIVERED', 'CANCELLED', 'FAILED'].includes(order.status);
  const isDelivered = order.status === 'DELIVERED';
  const isPartiallyShipped = order.fulfillmentStatus === 'PARTIAL' || order.status === 'SHIPPED';

  const activeProvider = order.overrideProvider ?? order.resolvedProvider;
  const activeWarehouse = order.overrideWarehouse ?? order.resolvedWarehouse;
  const addr = order.shippingAddress as Record<string, string> | null;
  const totalItems = order.items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <div className="space-y-6">
      {/* Header bar — back, order number, badges, refresh */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-surface px-4 py-3 sm:px-5">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/orders')}
            className="flex items-center justify-center rounded-md border border-border bg-surface p-2 text-text-secondary transition-colors hover:bg-surface-secondary hover:text-text-primary"
            title="Back to Orders"
            aria-label="Back to Orders"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-bold text-text-primary sm:text-xl">
                {order.orderNumber}
              </h1>
              <Badge
                variant={ORDER_STATUS_MERCHANT_VARIANT[order.status] ?? 'default'}
                size="sm"
              >
                {getMerchantOrderStatusLabel(order.status)}
              </Badge>
              {/* Label purchased, parcel not yet in transit — shown alongside
                  the In Production badge. Gated on the real Shipment row so a
                  manual admin status edit never fakes the chip. */}
              {order.shipments?.some((s) => s.status === 'LABEL_CREATED') && (
                <Badge variant="info" size="sm">
                  Label Created
                </Badge>
              )}
              {isAllSelf && (
                <Badge variant="warning" size="sm">
                  SELF-FULFILL
                </Badge>
              )}
              {isMixedFulfillment && (
                <Badge variant="info" size="sm">
                  MIXED FULFILLMENT
                </Badge>
              )}
              <Badge
                variant={checkoutMethod.type === 'PICKUP' ? 'info' : 'default'}
                size="sm"
              >
                {checkoutMethod.label}
              </Badge>
              {order.fulfillmentStatus && (
                <Badge
                  variant={
                    order.fulfillmentStatus === 'FULFILLED'
                      ? 'success'
                      : order.fulfillmentStatus === 'UNFULFILLED'
                        ? 'warning'
                        : 'default'
                  }
                  size="sm"
                >
                  {order.fulfillmentStatus.replace(/_/g, ' ')}
                </Badge>
              )}
            </div>
            <p className="mt-0.5 text-[11px] text-text-tertiary">
              {new Date(order.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}{' '}
              at{' '}
              {new Date(order.createdAt).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              })}{' '}
              from {order.source}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void orderQuery.refetch()}
          disabled={orderQuery.isFetching && !orderQuery.isLoading}
        >
          {orderQuery.isFetching && !orderQuery.isLoading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {/* Success banner — Polaris-aligned */}
      {successMessage && (
        <div className="flex items-center justify-between rounded-lg border px-4 py-3" style={{ backgroundColor: 'var(--color-banner-success-bg)', borderColor: 'var(--color-banner-success-border)' }}>
          <span className="text-sm font-medium" style={{ color: 'var(--color-banner-success-text)' }}>
            {successMessage}
          </span>
          <button
            type="button"
            onClick={() => setSuccessMessage(null)}
            className="ml-4 text-lg leading-none transition-opacity hover:opacity-70"
            style={{ color: 'var(--color-banner-success-text)' }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Awaiting merchant approval banner (manual fulfillment hold) */}
      {isAwaitingApproval && (
        <div
          className="rounded-lg border px-4 py-3 text-sm"
          style={{
            backgroundColor: 'var(--color-banner-warning-bg)',
            borderColor: 'var(--color-banner-warning-border)',
            color: 'var(--color-banner-warning-text)',
          }}
        >
          <strong>Awaiting your approval</strong> — approve this order in your
          Shopify admin via <strong>Request fulfillment</strong>. Production
          and billing start automatically once approved.
        </div>
      )}

      {/* Self-fulfilled order banner — no TK production, never billed */}
      {isAllSelf && (
        <div
          className="rounded-lg border px-4 py-3 text-sm"
          style={{
            backgroundColor: 'var(--color-banner-warning-bg)',
            borderColor: 'var(--color-banner-warning-border)',
            color: 'var(--color-banner-warning-text)',
          }}
        >
          <strong>Self-fulfilled</strong> — you handle printing and shipping
          for every item on this order. You are not charged for it; print
          files stay available below.
        </div>
      )}

      {/* Restriction toast — shown on demand when user clicks a restricted action */}
      {restrictionToast && (
        <div className="flex items-center justify-between rounded-lg border px-4 py-3 text-sm animate-in fade-in slide-in-from-top-2 duration-200" style={{ backgroundColor: 'var(--color-banner-warning-bg)', borderColor: 'var(--color-banner-warning-border)', color: 'var(--color-banner-warning-text)' }}>
          <span>{restrictionToast}</span>
          <button
            type="button"
            onClick={() => { 
              if (restrictionTimer.current) clearTimeout(restrictionTimer.current); 
              setRestrictionToast(null); 
            }}
            className="ml-4 text-lg leading-none transition-opacity hover:opacity-70"
            style={{ color: 'var(--color-banner-warning-text)' }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* Left column — summary, payment, timeline */}
        <div className="space-y-4">
          <OrderSummaryCard
            orderNumber={order.orderNumber}
            status={order.status}
            provider={activeProvider}
            warehouse={activeWarehouse}
            items={order.items}
            createdAt={order.createdAt}
            onEditDesign={(itemId) => {
              const it = order.items.find((i) => i.id === itemId);
              if (it) editDesignMutation.mutate(it);
            }}
            onEditRestricted={showRestrictionToast}
            editPendingItemId={
              editDesignMutation.isPending
                ? editDesignMutation.variables?.id ?? null
                : null
            }
          />

          {/* Download / export actions — ZIP, summary PDF and regenerate in
              one responsive row. The summary PDF is ALWAYS offered (the
              endpoint self-heals a missing render); the ZIP slot reflects
              export readiness and hides when this audience has no
              downloadable files (e.g. POD-only orders). */}
          {(() => {
            const xs = exportStatusQuery.data?.data;
            const showZipSlot = hasDownloadableFiles && !(xs && xs.status === 'NONE');

            const downloadIcon = (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            );

            const summaryPdfLink = (
              <a
                href={`${import.meta.env.VITE_API_URL ?? ''}/api/v1/orders/${order.id}/summary.pdf`}
                target="_blank"
                rel="noreferrer"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-secondary"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                Order summary (PDF)
              </a>
            );

            const regenerateButton = hasDesignFiles && showZipSlot && (xs?.failed || xs?.ready) && (
              <button
                type="button"
                onClick={() => regenerateExportsMutation.mutate()}
                disabled={regenerateExportsMutation.isPending}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-60"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.356M19.5 14.652a8.25 8.25 0 01-15.038-2.158M4.5 9.348A8.25 8.25 0 0119.538 7.19M3 19.644V14.652h4.992" />
                </svg>
                {regenerateExportsMutation.isPending ? 'Regenerating…' : 'Regenerate exports'}
              </button>
            );

            let zipSlot: ReactNode = null;
            if (showZipSlot) {
              if (xs?.failed) {
                zipSlot = (
                  <div className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-danger/40 bg-danger/5 px-4 py-2.5 text-sm font-medium text-danger">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    Print file export failed
                  </div>
                );
              } else if (xs?.ready) {
                zipSlot = (
                  <a
                    href={`${import.meta.env.VITE_API_URL ?? ''}/api/v1/orders/${order.id}/export.zip`}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-secondary"
                  >
                    {downloadIcon}
                    Download all print files (ZIP)
                  </a>
                );
              } else {
                // PENDING / loading → preparing state (download disabled).
                zipSlot = (
                  <div
                    className="flex flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-lg border border-border bg-surface-secondary px-4 py-2.5 text-sm font-medium text-text-secondary"
                    aria-busy="true"
                  >
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Preparing print files…
                  </div>
                );
              }
            }

            return (
              <div className="flex flex-col gap-2 sm:flex-row">
                {zipSlot}
                {summaryPdfLink}
                {regenerateButton}
              </div>
            );
          })()}

          {/* Fulfillment & Shipments */}
          {order.shipments && order.shipments.length > 0 ? (
            <Card>
              <CardHeader title={`Shipments (${order.shipments.length})`} />
              <div className="space-y-3 px-4 pb-4 sm:px-5 sm:pb-5">
                {order.shipments.map((s) => {
                  const itemNames = s.items.map((si) => {
                    const oi = order.items.find((i) => i.id === si.orderItemId);
                    return oi ? `${orderItemDisplayName(oi)} ×${si.quantity}` : `Item ×${si.quantity}`;
                  });
                  return (
                    <div key={s.id} className="rounded-lg border border-border p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {s.carrier && (
                            <Badge variant="info" size="sm">{s.carrier}</Badge>
                          )}
                          <Badge
                            variant={
                              s.status === 'DELIVERED' ? 'success' :
                              s.status === 'IN_TRANSIT' || s.status === 'OUT_FOR_DELIVERY' ? 'primary' :
                              s.status === 'EXCEPTION' || s.status === 'RETURNED' ? 'danger' :
                              s.status === 'LABEL_CREATED' ? 'info' :
                              'default'
                            }
                            size="sm"
                          >
                            {s.status.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                        {s.shippedAt && (
                          <span className="text-[10px] text-text-tertiary">
                            {new Date(s.shippedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {s.trackingNumber && (
                        <div className="text-xs">
                          <span className="text-text-tertiary">Tracking: </span>
                          {s.trackingUrl ? (
                            <a
                              href={s.trackingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-brand-primary hover:underline"
                            >
                              {s.trackingNumber}
                            </a>
                          ) : (
                            <span className="font-mono">{s.trackingNumber}</span>
                          )}
                        </div>
                      )}
                      <div className="text-[11px] text-text-secondary">
                        {itemNames.join(', ')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ) : !isTerminal ? (
            <Card>
              <CardHeader title="Shipments" />
              <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                <p className="text-xs text-text-tertiary">No shipment information yet.</p>
              </div>
            </Card>
          ) : null}

          {/* Partial Fulfillment Summary */}
          {order.fulfillmentStatus && order.fulfillmentStatus !== 'UNFULFILLED' && (
            <Card>
              <CardHeader title="Fulfillment" />
              <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                <Badge
                  variant={
                    order.fulfillmentStatus === 'FULFILLED' ? 'success' :
                    order.fulfillmentStatus === 'PARTIAL' ? 'warning' : 'default'
                  }
                  size="md"
                >
                  {order.fulfillmentStatus === 'FULFILLED' ? 'Fulfilled' :
                   order.fulfillmentStatus === 'PARTIAL' ? 'Partially Fulfilled' : order.fulfillmentStatus}
                </Badge>
                {order.items.some((i) => (i.cancelledQty ?? 0) > 0) && (
                  <div className="mt-2 space-y-1">
                    {order.items.filter((i) => (i.cancelledQty ?? 0) > 0).map((i) => (
                      <p key={i.id} className="text-[11px] text-text-secondary">
                        {orderItemDisplayName(i)}: <span className="text-red-500">{i.cancelledQty} cancelled</span>
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          )}

          <Card>
            <CardHeader title="Customer paid (Shopify)" />
            <div className="space-y-1.5 px-4 pb-4 pt-0 sm:px-5 sm:pb-5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-text-secondary">
                  Subtotal ({totalItems} item{totalItems !== 1 ? 's' : ''})
                </span>
                <span className="text-text-primary">${order.subtotal}</span>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-1.5 text-xs font-bold">
                <span className="text-text-primary">Total</span>
                <span className="text-text-primary">
                  ${order.total} {order.currency}
                </span>
              </div>
            </div>
          </Card>

          {isAllSelf ? (
            <Card>
              <CardHeader title="Wholesale cost" />
              <div className="px-4 pb-4 pt-0 sm:px-5 sm:pb-5">
                <p className="text-xs text-text-secondary">
                  This order is fully self-fulfilled, so Transfer Kingdom does
                  not bill you for it.
                </p>
              </div>
            </Card>
          ) : (
            <WholesaleCostCard
              productionCost={order.wholesaleProductionCost ?? null}
              shippingCost={order.wholesaleShippingCost ?? null}
              totalCost={order.wholesaleTotalCost ?? null}
              currency={order.currency}
              computedAt={order.wholesaleCostComputedAt ?? null}
              customerPaidTotal={order.total}
              chargeStatus={order.wholesaleChargeStatus ?? null}
              holdReason={order.chargeHoldReason ?? order.lastChargeFailureCode ?? null}
              onRetry={() => chargeMutation.mutate()}
              retrying={chargeMutation.isPending}
            />
          )}

          {/* Revision history — post-order design edits, shown directly above
              the order-event timeline. Rendered whenever the order has any
              design-editable line or at least one captured revision. */}
          {(() => {
            const revisions = revisionsQuery.data?.data ?? [];
            const hasEditableLine = order.items.some(
              (it) => it.designContext === 'STOREFRONT' && it.designId,
            );
            if (revisions.length === 0 && !hasEditableLine) {
              return null;
            }
            const apiBase = import.meta.env.VITE_API_URL ?? '';
            return (
              <Card>
                <CardHeader title="Revision history" />
                <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                  <RevisionTimeline
                    revisions={revisions}
                    orderId={order.id}
                    apiBase={apiBase}
                  />
                </div>
              </Card>
            );
          })()}

          <Card>
            <CardHeader title="Order history" />
            <div className="px-4 pb-4 sm:px-5 sm:pb-5">
              <EventTimeline events={order.events} />
            </div>
          </Card>
        </div>

        {/* Right column — customer, actions */}
        <div className="space-y-4">
          <Card padding="none">
            {/* Customer name */}
            <div className="px-4 pt-4 pb-3 sm:px-5">
              <h3 className="text-sm font-semibold text-text-primary">Customer</h3>
              <p className="mt-1 text-sm text-text-primary">
                {order.customerName ?? '—'}
              </p>
            </div>

            <hr className="border-border" />

            {/* Contact information */}
            <div className="px-4 py-3 sm:px-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-text-tertiary mb-2">
                Contact information
              </p>
              {order.customerEmail ? (
                <a
                  href={`mailto:${order.customerEmail}`}
                  className="block text-sm text-brand-primary hover:underline truncate"
                >
                  {order.customerEmail}
                </a>
              ) : (
                <p className="text-sm italic text-text-tertiary">No email provided</p>
              )}
              {(order.customerPhone || addr?.phone) ? (
                <p className="text-sm text-text-secondary mt-0.5">{order.customerPhone || addr?.phone}</p>
              ) : (
                <p className="text-sm italic text-text-tertiary mt-0.5">No phone number</p>
              )}
            </div>

            <hr className="border-border" />

            {/* Shipping address */}
            <div className="px-4 py-3 sm:px-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                  {checkoutMethod.type === 'PICKUP' ? 'Pickup location' : 'Shipping address'}
                </p>
                {!isTerminal && (
                  <button
                    type="button"
                    onClick={() => {
                      if (canEditAddress) {
                        openAddressEdit();
                      } else {
                        showRestrictionToast(
                          `This order is in ${order.status.replace(/_/g, ' ').toLowerCase()} status — address changes are restricted.`,
                        );
                      }
                    }}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-primary bg-brand-50 hover:bg-brand-100 transition-colors"
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                    </svg>
                    Edit
                  </button>
                )}
              </div>
              {addr ? (
                <div className="space-y-0.5 text-sm leading-relaxed text-text-primary">
                  {(addr.name || order.customerName) && (
                    <p>{addr.name ?? order.customerName}</p>
                  )}
                  {/* Shopify orders store street1/street2; portal orders address1/address2 */}
                  {(addr.street1 || addr.address1) && <p>{addr.street1 || addr.address1}</p>}
                  {(addr.street2 || addr.address2) && <p>{addr.street2 || addr.address2}</p>}
                  <p>
                    {[addr.city, addr.province, addr.zip].filter(Boolean).join(', ')}
                  </p>
                  {addr.country && <p>{addr.country}</p>}
                  {addr.phone && <p className="text-text-secondary">{addr.phone}</p>}
                </div>
              ) : (
                <p className="text-sm italic text-text-tertiary">
                  No shipping address provided
                </p>
              )}
              {addr && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    [addr.street1 || addr.address1, addr.city, addr.province, addr.zip, addr.country].filter(Boolean).join(', '),
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-xs text-brand-primary hover:underline"
                >
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                  </svg>
                  View map
                </a>
              )}
            </div>
          </Card>

          {/* Actions — Cancel */}
          {canCancel && (
            <Card>
              <CardHeader title="Actions" />
              <div className="space-y-3 px-4 pb-4 sm:px-5 sm:pb-5">
                <div className="flex flex-wrap gap-3">
                  {canDirectCancel && (
                    <Button
                      variant="danger"
                      onClick={() => setCancelConfirmOpen(true)}
                      disabled={cancelMutation.isPending}
                    >
                      Cancel Order
                    </Button>
                  )}
                  {canRequestCancel && (
                    <Button
                      variant="outline"
                      onClick={() => setCancelConfirmOpen(true)}
                      disabled={cancelMutation.isPending}
                    >
                      Request Cancellation
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Return Request (available after delivery or partial shipment) */}
          {(isDelivered || isPartiallyShipped) && (
            <Card>
              <CardHeader title="Returns" />
              <div className="px-4 pb-4 sm:px-5 sm:pb-5 space-y-2">
                <p className="text-xs text-text-secondary">
                  Need to return an item? Contact support to initiate a return request.
                </p>
                <Button variant="outline" size="sm" disabled>
                  Request Return (Coming Soon)
                </Button>
              </div>
            </Card>
          )}

          <Card>
            <CardHeader title="Tags" />
            <div className="space-y-2 px-4 pb-4 sm:px-5 sm:pb-5">
              {order.source === 'SHOPIFY' && (
                <p className="text-[11px] text-text-tertiary">
                  Tags sync with your Shopify order — edits here appear in Shopify admin and vice versa.
                </p>
              )}
              {(order.tags ?? []).length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {(order.tags ?? []).map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-0.5 rounded-full border border-border bg-surface-secondary px-2 py-0.5 text-[11px] text-text-secondary"
                    >
                      {tag}
                      {canEditTags && (
                        <button
                          type="button"
                          onClick={() => removeTagMutation.mutate(tag)}
                          disabled={removeTagMutation.isPending}
                          className="ml-0.5 text-text-tertiary transition-colors hover:text-text-primary leading-none"
                          title={`Remove "${tag}"`}
                          aria-label={`Remove tag ${tag}`}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-text-tertiary">No tags yet.</p>
              )}
              {canEditTags && (
                <form
                  onSubmit={(e: FormEvent) => {
                    e.preventDefault();
                    const val = tagInput.trim();
                    if (!val) return;
                    addTagMutation.mutate(val);
                    setTagInput('');
                  }}
                  className="flex gap-1.5"
                >
                  <Input
                    placeholder="Add tag..."
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    maxLength={SHOPIFY_MAX_TAG_LENGTH}
                    className="flex-1"
                    aria-label="New order tag"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    type="submit"
                    disabled={!tagInput.trim() || addTagMutation.isPending}
                    loading={addTagMutation.isPending}
                  >
                    Add
                  </Button>
                </form>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Cancel confirmation modal */}
      <Modal
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        title={canRequestCancel ? 'Request Cancellation' : 'Cancel Order'}
        description={
          canRequestCancel
            ? `Order ${order.orderNumber} is in production. A cancellation request will be sent to the team.`
            : `Are you sure you want to cancel order ${order.orderNumber}? This action may not be reversible.`
        }
        size="sm"
      >
        {cancelMutation.isError && (
          <div className="mb-4 rounded-lg border px-4 py-3 text-sm" style={{ backgroundColor: 'var(--color-banner-critical-bg)', borderColor: 'var(--color-banner-critical-border)', color: 'var(--color-banner-critical-text)' }}>
            {cancelMutation.error instanceof ApiClientError && cancelMutation.error.apiError?.details?.length
              ? cancelMutation.error.apiError.details.map((d) => d.message).join('. ')
              : (cancelMutation.error as Error)?.message ?? 'Failed to cancel order.'}
          </div>
        )}
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => setCancelConfirmOpen(false)}
            disabled={cancelMutation.isPending}
          >
            Keep Order
          </Button>
          <Button
            variant="danger"
            onClick={() => cancelMutation.mutate()}
            loading={cancelMutation.isPending}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending
              ? 'Processing…'
              : canRequestCancel ? 'Request Cancellation' : 'Cancel Order'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Address edit modal */}
      <Modal
        open={addressEditOpen}
        onClose={() => setAddressEditOpen(false)}
        title="Edit Shipping Address"
        size="md"
      >
        <div className="space-y-3">
          {addressMutation.isError && (
            <div className="rounded-lg border px-4 py-3 text-sm" style={{ backgroundColor: 'var(--color-banner-critical-bg)', borderColor: 'var(--color-banner-critical-border)', color: 'var(--color-banner-critical-text)' }}>
              {addressMutation.error instanceof ApiClientError && addressMutation.error.apiError?.details?.length
                ? addressMutation.error.apiError.details.map((d) => d.message).join('. ')
                : (addressMutation.error as Error)?.message ?? 'Failed to update address.'}
            </div>
          )}
          <Input
            label="Full Name"
            value={addressForm.name}
            onChange={(e) => setAddressForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="John Doe"
          />
          <Input
            label="Address Line 1"
            value={addressForm.address1}
            onChange={(e) => setAddressForm((p) => ({ ...p, address1: e.target.value }))}
            placeholder="123 Main St"
          />
          <Input
            label="Address Line 2"
            value={addressForm.address2}
            onChange={(e) => setAddressForm((p) => ({ ...p, address2: e.target.value }))}
            placeholder="Apt 4B"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="City"
              value={addressForm.city}
              onChange={(e) => setAddressForm((p) => ({ ...p, city: e.target.value }))}
              placeholder="Houston"
            />
            <Input
              label="State / Province"
              value={addressForm.province}
              onChange={(e) => setAddressForm((p) => ({ ...p, province: e.target.value }))}
              placeholder="TX"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="ZIP / Postal Code"
              value={addressForm.zip}
              onChange={(e) => setAddressForm((p) => ({ ...p, zip: e.target.value }))}
              placeholder="77001"
            />
            <Input
              label="Country"
              value={addressForm.country}
              onChange={(e) => setAddressForm((p) => ({ ...p, country: e.target.value }))}
              placeholder="US"
            />
          </div>
          <Input
            label="Phone"
            value={addressForm.phone}
            onChange={(e) => setAddressForm((p) => ({ ...p, phone: e.target.value }))}
            placeholder="+1 555-123-4567"
          />
        </div>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => setAddressEditOpen(false)}
            disabled={addressMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddressSave}
            loading={addressMutation.isPending}
            disabled={addressMutation.isPending || !addressForm.address1.trim()}
          >
            Save Address
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
